import type { MaybeRefOrGetter } from 'vue'

export interface InlineEditableListValidatableRow {
  isInvalid: MaybeRefOrGetter<boolean>
  validate: () => Promise<boolean>
}

export interface UseInlineEditableListFormActionsOptions {
  handleSubmit: () => Promise<void>
  isLoading: MaybeRefOrGetter<boolean>
  resetState: () => void
  rowRefs: MaybeRefOrGetter<InlineEditableListValidatableRow[]>
  saveButtonDisabled: MaybeRefOrGetter<boolean>
  stateHasChanged: MaybeRefOrGetter<boolean>
}

export const useInlineEditableListFormActions = (options: UseInlineEditableListFormActionsOptions) => {
  const showUnsavedChangesModal = ref(false)

  const hasInvalidRows = computed(() => {
    const rows = toValue(options.rowRefs).filter(Boolean)
    return rows.some((row) => toValue(row.isInvalid))
  })

  const isSaveDisabled = computed(() => toValue(options.saveButtonDisabled) || hasInvalidRows.value)

  const validateAllRows = async () => {
    const rows = toValue(options.rowRefs).filter(Boolean)
    const results = await Promise.all(rows.map((row) => row.validate()))

    return results.every(Boolean)
  }

  const handleSaveClick = async () => {
    const isValid = await validateAllRows()
    if (!isValid) {
      return
    }

    await options.handleSubmit()
  }

  const handleCancelClick = () => {
    if (!toValue(options.stateHasChanged)) {
      return
    }

    showUnsavedChangesModal.value = true
  }

  const handleUnsavedConfirm = async () => {
    const isValid = await validateAllRows()
    if (!isValid) {
      return
    }

    await options.handleSubmit()
    showUnsavedChangesModal.value = false
  }

  const handleUnsavedDiscard = () => {
    options.resetState()
    showUnsavedChangesModal.value = false
  }

  return {
    handleCancelClick,
    handleSaveClick,
    handleUnsavedConfirm,
    handleUnsavedDiscard,
    hasInvalidRows,
    isSaveDisabled,
    showUnsavedChangesModal,
    validateAllRows,
  }
}
